// ── OwnerAvatar.jsx ──────────────────────────────────────────────────────────
// أفاتار مالك الموقع — إطار ملكي زخرفي احترافي بمستوى BigoBigo / Yango Play

import React from "react";
import "./OwnerAvatar.css";

/**
 * OwnerAvatar — أفاتار مالك الموقع الحصري
 *
 * Props:
 *   letter     — الحرف الأول للاسم (يُعرض إن لم توجد صورة)
 *   avatarUrl  — رابط الصورة الشخصية (اختياري)
 *   size       — حجم الأفاتار بالـ px (افتراضي: 36)
 *   showCrown  — هل تظهر أيقونة التاج؟ (افتراضي: true)
 */
const OwnerAvatar = ({ letter = "O", avatarUrl = "", size = 36, showCrown = true }) => {
  const [imgError, setImgError] = React.useState(false);

  React.useEffect(() => { setImgError(false); }, [avatarUrl]);

  const crownW = Math.round(size * 0.72);
  const crownH = Math.round(size * 0.46);
  const shellSize = size + 36;
  const dotSize = Math.max(4, Math.round(size * 0.095));

  return (
    <div
      className="oa-root"
      style={{ width: shellSize, height: shellSize }}
      aria-label="Owner Avatar"
    >
      <div
        className="oa-frame-shell"
        style={{ width: shellSize, height: shellSize }}
      >
        <div
          className="oa-glow-ring"
          style={{ width: shellSize, height: shellSize }}
        />
        <DecorativeFrameOuter size={shellSize} />
        <DecorativeFrameInner size={shellSize} avatarSize={size} />
        <div
          className="oa-shimmer-ring"
          style={{ width: shellSize + 6, height: shellSize + 6, inset: -3 }}
        />
        <div
          className="oa-orbit-dots"
          style={{ width: shellSize, height: shellSize }}
        >
          {[0, 1, 2, 3].map(i => (
            <div
              key={i}
              className="oa-orbit-dot"
              style={{ width: dotSize, height: dotSize }}
            />
          ))}
        </div>

        {avatarUrl && !imgError ? (
          <div
            className="oa-img-wrap"
            style={{ width: size, height: size, position: "relative", zIndex: 2 }}
          >
            <img
              src={avatarUrl}
              alt="owner"
              className="oa-img"
              onError={() => setImgError(true)}
              draggable={false}
            />
          </div>
        ) : (
          <div
            className="oa-letter-wrap"
            style={{ width: size, height: size, position: "relative", zIndex: 2 }}
          >
            <div className="oa-bg" />
            <span
              className="oa-letter"
              style={{ fontSize: Math.round(size * 0.42) }}
            >
              {letter}
            </span>
          </div>
        )}
      </div>

      {showCrown && (
        <div className="oa-crown" aria-hidden="true">
          <CrownSVG width={crownW} height={crownH} />
        </div>
      )}
    </div>
  );
};

const DecorativeFrameOuter = ({ size }) => {
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 1;
  const frameR = size / 2 - 5;
  const tickR  = frameR - 2;
  const gemR   = size / 2 - 3;

  const ticks = [];
  const TICK_COUNT = 36;
  for (let i = 0; i < TICK_COUNT; i++) {
    const angle = (i * 360) / TICK_COUNT;
    const rad   = (angle * Math.PI) / 180;
    const isMajor = i % 9 === 0;
    const len   = isMajor ? 5 : (i % 3 === 0 ? 3 : 2);
    const x1 = cx + tickR * Math.cos(rad);
    const y1 = cy + tickR * Math.sin(rad);
    const x2 = cx + (tickR - len) * Math.cos(rad);
    const y2 = cy + (tickR - len) * Math.sin(rad);
    ticks.push({ x1, y1, x2, y2, isMajor });
  }

  const cornerGems = [45, 135, 225, 315].map(angle => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + gemR * Math.cos(rad), y: cy + gemR * Math.sin(rad) };
  });

  const cardinalDots = [0, 90, 180, 270].map(angle => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + gemR * Math.cos(rad), y: cy + gemR * Math.sin(rad) };
  });

  return (
    <svg
      className="oa-deco-frame"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={`oa-og-${size}`} x1="0" y1="0" x2={size} y2={size} gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#FFF0A0" />
          <stop offset="25%"  stopColor="#FFD700" />
          <stop offset="50%"  stopColor="#FF9500" />
          <stop offset="75%"  stopColor="#FFD700" />
          <stop offset="100%" stopColor="#FFF0A0" />
        </linearGradient>
        <radialGradient id={`oa-cg-${size}`} cx="38%" cy="32%" r="60%">
          <stop offset="0%"   stopColor="#AAFFCC" />
          <stop offset="40%"  stopColor="#00CC66" />
          <stop offset="100%" stopColor="#005522" />
        </radialGradient>
        <radialGradient id={`oa-cd-${size}`} cx="38%" cy="32%" r="60%">
          <stop offset="0%"   stopColor="#FFE566" />
          <stop offset="50%"  stopColor="#FFB800" />
          <stop offset="100%" stopColor="#885500" />
        </radialGradient>
        <filter id={`oa-gf-${size}`} x="-8%" y="-8%" width="116%" height="116%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" result="blur" />
          <feFlood floodColor="#FFB800" floodOpacity="0.65" result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id={`oa-gg-${size}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="1" result="blur" />
          <feFlood floodColor="#00FF88" floodOpacity="0.7" result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      <circle cx={cx} cy={cy} r={outerR}
        stroke={`url(#oa-og-${size})`} strokeWidth="1.5" fill="none"
        filter={`url(#oa-gf-${size})`} opacity="0.9" />

      <circle cx={cx} cy={cy} r={frameR - 6}
        stroke="rgba(255,200,0,0.25)" strokeWidth="0.7" fill="none" />

      {ticks.map((t, i) => (
        <line key={i}
          x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={t.isMajor ? "rgba(255,220,50,0.85)" : "rgba(255,190,0,0.45)"}
          strokeWidth={t.isMajor ? 1.4 : 0.7}
          strokeLinecap="round" />
      ))}

      {cornerGems.map((g, i) => (
        <g key={i} filter={`url(#oa-gg-${size})`}>
          <circle cx={g.x} cy={g.y} r={size * 0.045} fill={`url(#oa-cg-${size})`} />
          <circle cx={g.x - size * 0.012} cy={g.y - size * 0.012}
            r={size * 0.014} fill="rgba(255,255,255,0.75)" />
        </g>
      ))}

      {cardinalDots.map((d, i) => (
        <g key={i}>
          <circle cx={d.x} cy={d.y} r={size * 0.032}
            fill={`url(#oa-cd-${size})`} filter={`url(#oa-gf-${size})`} />
          <circle cx={d.x - size * 0.008} cy={d.y - size * 0.008}
            r={size * 0.01} fill="rgba(255,255,255,0.7)" />
        </g>
      ))}
    </svg>
  );
};

const DecorativeFrameInner = ({ size, avatarSize }) => {
  const cx     = size / 2;
  const cy     = size / 2;
  const innerR = (avatarSize / 2) + (size - avatarSize) / 2 * 0.38;
  const arcs   = [];
  const ARC_COUNT = 8;

  for (let i = 0; i < ARC_COUNT; i++) {
    const startAngle = (i * 360) / ARC_COUNT;
    const arcSpan    = 22;
    const s = ((startAngle - arcSpan / 2) * Math.PI) / 180;
    const e = ((startAngle + arcSpan / 2) * Math.PI) / 180;
    arcs.push({
      x1: cx + innerR * Math.cos(s),
      y1: cy + innerR * Math.sin(s),
      x2: cx + innerR * Math.cos(e),
      y2: cy + innerR * Math.sin(e),
      r: innerR,
      isEven: i % 2 === 0,
      midA: (i * 360) / ARC_COUNT,
    });
  }

  return (
    <svg
      className="oa-deco-frame-inner"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={`oa-ig-${size}`} x1="0" y1="0" x2={size} y2={size} gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#FFE566" stopOpacity="0.9" />
          <stop offset="50%"  stopColor="#FF9800" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FFE566" stopOpacity="0.9" />
        </linearGradient>
        <filter id={`oa-if-${size}`} x="-15%" y="-15%" width="130%" height="130%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" result="blur" />
          <feFlood floodColor="#FFD700" floodOpacity="0.6" result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {arcs.map((arc, i) => (
        <path key={i}
          d={`M ${arc.x1} ${arc.y1} A ${arc.r} ${arc.r} 0 0 1 ${arc.x2} ${arc.y2}`}
          stroke={arc.isEven ? `url(#oa-ig-${size})` : "rgba(255,180,0,0.35)"}
          strokeWidth={arc.isEven ? 2 : 1}
          strokeLinecap="round"
          fill="none"
          filter={arc.isEven ? `url(#oa-if-${size})` : undefined} />
      ))}

      {arcs.map((arc, i) => {
        if (!arc.isEven) return null;
        const rad = (arc.midA * Math.PI) / 180;
        return (
          <circle key={`sp-${i}`}
            cx={cx + innerR * Math.cos(rad)}
            cy={cy + innerR * Math.sin(rad)}
            r={size * 0.018}
            fill="rgba(255,240,100,0.8)"
            filter={`url(#oa-if-${size})`} />
        );
      })}
    </svg>
  );
};

const CrownSVG = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 56 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="oa-crown-body" x1="28" y1="0" x2="28" y2="36" gradientUnits="userSpaceOnUse">
        <stop offset="0%"   stopColor="#FFE87A" />
        <stop offset="38%"  stopColor="#FFD000" />
        <stop offset="72%"  stopColor="#E09000" />
        <stop offset="100%" stopColor="#C07800" />
      </linearGradient>
      <linearGradient id="oa-crown-base" x1="28" y1="22" x2="28" y2="36" gradientUnits="userSpaceOnUse">
        <stop offset="0%"   stopColor="#F0B800" />
        <stop offset="100%" stopColor="#9A6000" />
      </linearGradient>
      <linearGradient id="oa-crown-shine" x1="8" y1="22" x2="48" y2="26" gradientUnits="userSpaceOnUse">
        <stop offset="0%"   stopColor="rgba(255,255,220,0)" />
        <stop offset="40%"  stopColor="rgba(255,255,200,0.55)" />
        <stop offset="100%" stopColor="rgba(255,255,220,0)" />
      </linearGradient>
      <radialGradient id="oa-gem-red" cx="42%" cy="35%" r="58%">
        <stop offset="0%"   stopColor="#FF8099" />
        <stop offset="45%"  stopColor="#D8002E" />
        <stop offset="100%" stopColor="#7A0018" />
      </radialGradient>
      <radialGradient id="oa-gem-blue" cx="38%" cy="32%" r="62%">
        <stop offset="0%"   stopColor="#90CCFF" />
        <stop offset="50%"  stopColor="#0066DD" />
        <stop offset="100%" stopColor="#002888" />
      </radialGradient>
      <filter id="oa-crown-glow" x="-15%" y="-20%" width="130%" height="150%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" result="b" />
        <feFlood floodColor="#FFB800" floodOpacity="0.7" result="c" />
        <feComposite in="c" in2="b" operator="in" result="g" />
        <feMerge><feMergeNode in="g" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>
    <path d="M6 34 L6 22 L12 28 L20 10 L28 18 L36 10 L44 28 L50 22 L50 34 Q50 35.2 48.8 35.2 L7.2 35.2 Q6 35.2 6 34 Z"
      fill="url(#oa-crown-body)" filter="url(#oa-crown-glow)" />
    <rect x="6" y="22" width="44" height="5" fill="url(#oa-crown-shine)" />
    <path d="M6 28 L6 34 Q6 35.2 7.2 35.2 L48.8 35.2 Q50 35.2 50 34 L50 28 Z"
      fill="url(#oa-crown-base)" opacity="0.7" />
    <line x1="6"  y1="22.5" x2="50" y2="22.5" stroke="rgba(255,240,120,0.55)" strokeWidth="0.9" />
    <line x1="6"  y1="30"   x2="50" y2="30"   stroke="rgba(120,70,0,0.3)"     strokeWidth="0.8" />
    <circle cx="12"   cy="28"   r="3.2" fill="url(#oa-gem-blue)" />
    <circle cx="11.2" cy="26.9" r="1.1" fill="rgba(255,255,255,0.68)" />
    <circle cx="28"   cy="18"   r="4.2" fill="url(#oa-gem-red)" />
    <circle cx="26.8" cy="16.6" r="1.5" fill="rgba(255,255,255,0.72)" />
    <circle cx="26.2" cy="16.0" r="0.6" fill="rgba(255,255,255,0.9)" />
    <circle cx="44"   cy="28"   r="3.2" fill="url(#oa-gem-blue)" />
    <circle cx="43.2" cy="26.9" r="1.1" fill="rgba(255,255,255,0.68)" />
    <circle cx="20"   cy="10"   r="1.8" fill="#FFE566" opacity="0.75" />
    <circle cx="36"   cy="10"   r="1.8" fill="#FFE566" opacity="0.75" />
    <circle cx="28"   cy="13.5" r="0.85" fill="rgba(255,255,255,0.65)" />
    <line x1="23.5" y1="14" x2="22.5" y2="19"
      stroke="rgba(255,245,150,0.4)" strokeWidth="0.6" strokeLinecap="round" />
  </svg>
);

export default OwnerAvatar;
